/**
 * Password sign-in form (plan §2.3 · §2.4 · §2.5 · §2.5.1 · AD-2 · ADR-0027).
 *
 * Password is an ADDITIONAL credential, not a replacement: the passkey remains
 * the recovery path, which is why the rate-limit copy points at it.
 *
 * Three properties this file exists to hold:
 *
 *  1. The wire body is exactly `{ password }`. `PASSWORD_LOGIN_USERNAME` is a
 *     password-manager vault key (AD-2), not an account identifier, so it never
 *     leaves the browser.
 *  2. Authenticated state comes from `auth.refresh()` — the session GET — and
 *     never from the login response body. A 200 says the credential matched;
 *     only the session GET says a session exists.
 *  3. Errors are classified from the TYPED error's `status`, never by sniffing
 *     a message string. That is the defect Phase 1 removed from the passkey
 *     path, and re-introducing it here would make Safari's `TypeError: Load
 *     failed` read as a rejected credential.
 *
 * No bypass branch and no page chrome: LoginPanel (Phase 3) owns both, and a
 * second copy of either is a second thing to keep in step.
 */
import {
  useCallback,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type JSX,
} from 'react';

import type { AppConfig } from '@/config';

import { useAuth, type SessionProbe } from './auth-context';
import { AuthFetchError, AuthNetworkError, postAuthJson, reportUnexpected } from './auth-fetch';
import {
  PASSWORD_LOGIN_USERNAME,
  PASSWORD_MAX_LENGTH,
  PASSWORD_MIN_LENGTH,
} from './password-constants';
import {
  clearPasswordSync,
  describedByFor,
  isPasswordFieldError,
  validatePassword,
} from './password-field';
import styles from './password-login.module.css';
import { resolveReturnTo, type ReturnToLocation } from './return-to';

export interface PasswordLoginProps {
  readonly config: AppConfig;
  readonly returnTo?: ReturnToLocation | null;
  readonly onSuccess?: (returnTo: ReturnToLocation) => void;
  /**
   * Id of a node OUTSIDE this form that describes the password field.
   *
   * LoginPanel's `auto`-only pre-hash hint is the one caller: it explains that
   * a deployment may have no password set yet, which is a description of this
   * input, but it belongs to the panel because it is a statement about the
   * composition rather than about the form. Passing the id instead of moving
   * the copy keeps the panel's ownership and still gives the field a
   * programmatic description — a user who tabs straight into it, or re-queries
   * it after an error, would otherwise never meet the hint.
   */
  readonly describedBy?: string | undefined;
}

/*
 * 'generic' covers a 401, an unrecognised status AND an unconfirmed refresh on
 * purpose: each is a distinguishable server state, and giving any of them its
 * own copy tells an attacker which one they reached (security.md §3).
 */
type PasswordError = 'empty' | 'too-short' | 'too-long' | 'generic' | 'rate-limited' | 'network';

const RATE_LIMIT_STATUS = 429;

function classifyError(err: unknown): PasswordError {
  if (err instanceof AuthNetworkError) return 'network';
  if (err instanceof AuthFetchError && err.status === RATE_LIMIT_STATUS) return 'rate-limited';
  return 'generic';
}

/*
 * `reportUnexpected` now lives in `auth-fetch.ts` beside the two transport
 * error classes whose absence defines "unexpected", so the passkey ceremony
 * shares one policy with this form rather than having none. Behaviour here is
 * unchanged: no `isExpected` argument is passed, because every non-transport
 * throwable reaching this catch is a programming error.
 */

function errorMessage(error: PasswordError): string {
  if (error === 'empty') return 'Enter your password.';
  /* Composed, not typed: a literal here would survive a change to the constant
     and tell the user a number the form no longer enforces. */
  if (error === 'too-short') {
    return `Password must be at least ${String(PASSWORD_MIN_LENGTH)} characters.`;
  }
  /* Composed from the same shared constant the server enforces, so the number
     the user is told is the number that would have rejected them. Without the
     pre-flight this arrives as a 413 or a uniform 401 — copy whose only implied
     remedy is to retry, which resends the same over-long value. */
  if (error === 'too-long') {
    return `Password must be at most ${String(PASSWORD_MAX_LENGTH)} characters.`;
  }
  /*
   * Instruction only, and deliberately WITHOUT a duration.
   *
   * Two independent producers return this status and the client cannot tell
   * them apart — both send the same byte-identical body. The ADR-0011
   * consecutive-failure ban (failure-ban.ts) runs 5 minutes and also covers
   * the passkey route; the @fastify/rate-limit throttle (plugins/rate-limit.ts)
   * clears in under a minute and does not touch the passkey route at all. Any
   * concrete number is therefore wrong for one of them. Naming one would also
   * duplicate a server security constant across a forbidden import edge, with
   * nothing to keep the two in step, and describing how the ban clears reports
   * progress towards the reset. A duration can only come from the wire.
   */
  if (error === 'rate-limited') {
    return 'Too many sign-in attempts. Try again later, or sign in with your passkey.';
  }
  if (error === 'network') return 'Network error - try again.';
  return 'Sign-in failed. Try again.';
}

/*
 * `isPasswordFieldError`, `validatePassword`, `describedByFor` and
 * `clearPasswordSync` come from `password-field.ts` — this file and
 * `set-password.tsx` each had a byte-identical copy.
 *
 * Why THIS surface excludes 'generic' from `aria-invalid`, which the shared
 * predicate delivers by listing only the three value arms: 'generic' covers a
 * rejected credential AND a 500 AND an unconfirmed session, and only the first
 * is a property of the input. Marking the field invalid across all three would
 * tell assistive tech the value is wrong when it may be exactly right, and the
 * user's correct response is to wait, not to retype.
 *
 * That is a semantic choice, NOT a security-forced one: because 'generic'
 * already collapses the three states into one message, marking the field
 * invalid across them uniformly would disclose nothing either. §3 is satisfied
 * by the copy, not by the ARIA state. WCAG 3.3.1 is met regardless — the field
 * is identified via `aria-describedby` and the error is described in text;
 * `aria-invalid` is technique ARIA21, sufficient rather than required.
 */

/**
 * `inFlight` is a ref, not the `busy` state, and that is what makes a double
 * submit one POST.
 *
 * `disabled={busy}` only protects the BUTTON — Enter and any form-level submit
 * event bypass it entirely. And a guard reading `busy` reads a RENDER closure:
 * React 18 batches every event in a task, so two submits in one batch both see
 * `busy === false` and both POST. A ref is written and read synchronously, so
 * the property holds regardless of when React re-renders (§2.5.1). The cost of
 * getting it wrong is two of the three consecutive-failure slots (ADR-0011)
 * spent on one user action.
 */
interface PasswordSubmitOpts {
  readonly config: AppConfig;
  readonly credential: { readonly password: string; readonly setPassword: (next: string) => void };
  readonly onSettled: {
    readonly refresh: () => Promise<SessionProbe>;
    readonly onSuccess: (() => void) | undefined;
  };
}

interface PasswordSubmitState {
  readonly busy: boolean;
  readonly error: PasswordError | null;
  readonly setError: (next: PasswordError | null) => void;
  readonly submit: () => Promise<void>;
}

/**
 * Owns the transient submit state and the POST itself.
 *
 * Extracted so `PasswordLogin` stays inside the 50-line limit (style §3), and
 * so this surface has the same shape as `useCeremonySubmit` in
 * passkey-login.tsx — the two credential paths now differ only where they are
 * meant to.
 */
function usePasswordSubmit(opts: PasswordSubmitOpts): PasswordSubmitState {
  const { config, credential, onSettled } = opts;
  const { password, setPassword } = credential;
  const { refresh, onSuccess } = onSettled;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<PasswordError | null>(null);
  const inFlight = useRef(false);

  const submit = useCallback(async () => {
    if (inFlight.current) return;
    setError(null);
    const invalid = validatePassword(password);
    if (invalid !== null) {
      setError(invalid);
      return;
    }
    /* Written BEFORE the first await, so the guard above is already closed when
       a second submit from the same batch reaches it. `busy` drives the
       disabled attribute only — it is presentation, not the lock. */
    inFlight.current = true;
    setBusy(true);
    let confirmed = false;
    try {
      await postAuthJson(config, 'password/login', { password });
      confirmed = (await refresh()) === 'authenticated';
      if (!confirmed) setError('generic');
    } catch (err) {
      const classified = classifyError(err);
      setError(classified);
      /* The value has no further use on this arm — the user is told to stop and
         switch credentials, not to retry — so it does not outlive the attempt.
         The arms that DO invite a retry ('generic', 'network') keep it, since
         clearing there would make the user retype a password that may be
         correct. */
      if (classified === 'rate-limited') clearPasswordSync(setPassword);
      reportUnexpected(err);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
    if (!confirmed) return;
    /*
     * Cleared before navigating, not left to unmount. React assigns a
     * controlled input's value to `node.defaultValue`, which REFLECTS to the
     * value content attribute — so until this runs the plaintext is in the
     * serialized DOM (`outerHTML`, any attribute MutationObserver), not only in
     * the DOM property. Nothing in this app serializes the DOM today; this is
     * cheap insurance against the first thing that does, and the same reasoning
     * applies to the S2 set-password dialog, which runs authenticated.
     *
     * The clear is COMMITTED here, not queued — see `clearPasswordSync`, which
     * is what makes the guarantee above true rather than aspirational.
     */
    clearPasswordSync(setPassword);
    /*
     * Navigation is deliberately OUTSIDE the classified block. Inside it, a
     * router callback that threw would be caught by `classifyError` and
     * rendered as 'Sign-in failed. Try again.' — telling a user who already
     * holds a confirmed session that their password was rejected, and sending
     * them to retype and retransmit it. A navigation the parent could not
     * perform is not an authentication outcome (§2.4).
     */
    onSuccess?.();
  }, [config, onSuccess, password, refresh, setError, setPassword]);

  return { busy, error, setError, submit };
}

export function PasswordLogin({
  config,
  returnTo,
  onSuccess,
  describedBy,
}: PasswordLoginProps): JSX.Element {
  const auth = useAuth();
  /* `useId`, not a module constant: two PasswordLogin instances on one page —
     which Phase 3 composition makes possible — would otherwise share an id and
     point every field at the first alert. */
  const errorId = useId();
  const [password, setPassword] = useState('');
  const notifySuccess = useCallback(() => {
    onSuccess?.(resolveReturnTo(returnTo ?? null));
  }, [onSuccess, returnTo]);
  const { busy, error, setError, submit } = usePasswordSubmit({
    config,
    credential: { password, setPassword },
    onSettled: { refresh: auth.refresh, onSuccess: notifySuccess },
  });

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      /* Native submit, prevented: Enter keeps working without a keydown
         handler, which is the whole reason this is a <form> (§2.5.1). */
      event.preventDefault();
      void submit();
    },
    [submit],
  );

  /* `setError` is listed because it now crosses a hook boundary: React's own
     useState setter is stable, but ESLint can only prove that for a setter
     declared in this scope. It is stable, so the dep is free. */
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setPassword(event.target.value);
      setError(null);
    },
    [setError],
  );

  const formProps = { errorId, password, busy, error, handleChange, handleSubmit, describedBy };
  return <PasswordForm {...formProps} />;
}

/**
 * Presentation only — every decision above it, nothing but markup here.
 *
 * The form's `aria-label` is load-bearing, not decoration. An UNNAMED `<form>`
 * is not exposed as a landmark by assistive tech, so before it the composed
 * panel had zero landmarks and one heading: with `passkeyMode='auto'` the only
 * boundary between the password field and the passkey handle/display-name
 * fields was LoginPanel's "or" divider, which is `aria-hidden`. A screen-reader
 * user met Password, Handle and Display name as one undifferentiated run.
 * Naming both surfaces is what lets that divider stay decorative.
 */
function PasswordForm({
  errorId,
  password,
  busy,
  error,
  handleChange,
  handleSubmit,
  describedBy,
}: {
  readonly errorId: string;
  readonly password: string;
  readonly busy: boolean;
  readonly error: PasswordError | null;
  readonly handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
  readonly handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  readonly describedBy?: string | undefined;
}): JSX.Element {
  const describedByIds = describedByFor(describedBy, error !== null, errorId);
  return (
    <form className={styles['root']} onSubmit={handleSubmit} aria-label="Password sign-in">
      <UsernameAssist />
      <label className={styles['label']}>
        Password
        <input
          className={styles['input']}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={handleChange}
          /*
           * Both are omitted rather than set false/empty when there is no
           * error. `aria-describedby` could now safely stay set — the region it
           * names is mounted permanently — but pointing at an EMPTY region
           * gives the field a description that says nothing, so it is still
           * attached only while there is something to describe.
           */
          {...(error !== null && isPasswordFieldError(error) ? { 'aria-invalid': true } : {})}
          {...(describedByIds === '' ? {} : { 'aria-describedby': describedByIds })}
        />
      </label>
      {/*
       * The live region is mounted ALWAYS, with only its contents conditional.
       * A `role="alert"` node that is itself injected is announced by most
       * browser/SR pairs but is a known miss for some, and it is at its least
       * reliable when the region never pre-exists. Registering it at mount
       * removes that question. The id lives on the REGION, not the message, so
       * `aria-describedby` can never dangle (`aria-valid-attr-value`) no matter
       * which errors come and go.
       */}
      <div id={errorId} role="alert">
        {error !== null ? <p className={styles['error']}>{errorMessage(error)}</p> : null}
      </div>
      <button type="submit" className={styles['button']} disabled={busy}>
        {busy ? 'Signing in…' : 'Sign in'}
      </button>
      {/*
       * A disabled button is blurred by the browser and dropped from the a11y
       * tree, so the 'Signing in…' name change above announces nothing and
       * focus falls to <body>. This region is what actually satisfies WCAG
       * 4.1.3 for the pending state; it is separate from the error alert
       * because a polite status must not preempt an assertive error.
       */}
      <p className="visually-hidden" role="status">
        {busy ? 'Signing in…' : ''}
      </p>
    </form>
  );
}

/**
 * AD-2 password-manager assist — the markup is pinned, not stylistic.
 *
 * A manager files a saved credential under the username field of the form it
 * saw, so without one it either skips the save or keys the entry on an empty
 * string. `type="hidden"` and `display: none` are both ignored by the heuristic,
 * which is why this is a real, laid-out input clipped by the global
 * `visually-hidden` class (styles/a11y.css) rather than removed from layout.
 *
 * The trade: `aria-hidden` keeps a vault key — which is NOT this user's account
 * handle — out of the a11y tree, where it would be read as one; `tabIndex={-1}`
 * pays for that by keeping the node unfocusable, without which it is an
 * `aria-hidden-focus` violation. `readOnly` over `disabled` because a disabled
 * field is not submitted and, in several managers, not autofilled either.
 *
 * `id` is part of that pinned markup, not incidental: `aria-hidden` removes the
 * accessible name and there is no `<label>`, so `autoComplete` and `id` are the
 * only signals left for a manager's field heuristic to score. It does not reach
 * the a11y tree (the node is `aria-hidden`).
 *
 * A `name` would score higher still, and is deliberately NOT set — see the
 * 'gives neither input a name attribute' test, which pins the absence: a named
 * field is serialized by a native submission, so omitting it is what keeps the
 * plaintext out of the URL, history and Referer if one ever escaped
 * `preventDefault`. That guarantee outranks the heuristic.
 */
function UsernameAssist(): JSX.Element {
  const assistId = useId();
  return (
    <input
      className="visually-hidden"
      type="text"
      id={assistId}
      value={PASSWORD_LOGIN_USERNAME}
      readOnly
      tabIndex={-1}
      aria-hidden="true"
      autoComplete="username"
    />
  );
}
